// @ts-strict-ignore
import { deleteProperty, setProperty } from 'dot-prop';
import { WorkflowEngineException } from '../../engine/schema';
import type { SchemaInstance, Threshold } from '../../engine/types';
import {
  assignDefaultsToThreshold,
  updateBooleanRuleWithNewThreshold,
  modifyRelevantDataFromThresholdRemoval,
  assignDynamicComparisonRightSide,
  removeDynamicComparisonRightSide,
  updateThresholdValuesBasedOnDataType,
} from '../../engine/update/thresholds';
import { generatePublicId } from '../../engine/utils';
import { ReduxErrorTypes } from '../constants';
import { snapshotAndCloneWorkflowData, compileWorkflowDataDependencies } from '../helpers';
import type { WorkflowState } from '../state';
import { isSelfReferencingThreshold } from '../../../service/thresholds';
import { captureSelfReferencingThresholdError } from '../../engine/validation/thresholds';
import { buildGraph, getAllNodeIdsConnectedToNode, getBidirectionalGraphTopology } from '../../../service/graph';

// ================ Threshold Reducers ================

/* Unopinionated version of createThreshold that offloads threshold & snapshot creation */
export function addThreshold(
  state: WorkflowState,
  action: {
    payload: {
      threshold: Threshold;
    };
  },
) {
  const { threshold } = action.payload;
  state.workflowData.thresholds[threshold.publicId] = threshold;
}

export function appendThresholdToLinkedAction(
  state: WorkflowState,
  action: {
    payload: {
      thresholdId: Threshold['publicId'];
    };
  },
) {
  const { thresholdId } = action.payload;
  state.linkedChildAction.thresholdIds.push(thresholdId);
}

export function removeThresholdFromLinkedAction(
  state: WorkflowState,
  action: {
    payload: {
      thresholdId: Threshold['publicId'];
    };
  },
) {
  const { thresholdId } = action.payload;
  state.linkedChildAction.thresholdIds = (
    state.linkedChildAction.thresholdIds.filter(id => id !== thresholdId)
  );
}

/*
* Unopinionated version of removeThreshold that offloads snapshot creation and
* only removes the threshold from threshold state.
*/
export function deleteThreshold(
  state: WorkflowState,
  action: {
    payload: { id: string };
  },
) {
  const { id: thresholdId } = action.payload;
  const threshold = state.workflowData.thresholds[thresholdId];

  if (!threshold) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_THRESHOLD_ID);
  }

  deleteProperty(state.workflowData.thresholds, thresholdId);
}

/* Unopinionated version of updateThreshold that offloads business logic */
export function updateThresholdAgnostic(
  state: WorkflowState,
  action: {
    payload: {
      threshold: Threshold;
    };
  },
) {
  const { threshold } = action.payload;

  if (!state.workflowData.thresholds[threshold.publicId]) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_THRESHOLD_ID);
  }

  state.workflowData.thresholds[threshold.publicId] = threshold;
}

export function updateWorkflowDataDependencies(state: WorkflowState) {
  state.workflowData = compileWorkflowDataDependencies(state.workflowData);
}

// todo BUILD-183 - refactor this reducer
export function createThreshold(
  state: WorkflowState,
  action: {
    payload: {
      thresholdOwnerActionId?: string;
      isPreview?: boolean;
      path?: string;
      value: {
        leftActionId?: string;
        actionId?: string;
      };
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { workflowProps, ui, featureFlagsForStore } = state;
  const {
    isPreview, path: payloadPath, value, thresholdOwnerActionId,
  } = action.payload;
  /**
   * `path` does not need to be passed to create a new threshold, so if there is no
   * `path` in the action `payload` create a default one with a new `thresholdId` that
   * will get picked up by the `split` below
   */
  const path = payloadPath || `${generatePublicId('threshold')}.left.actionId`;
  const [thresholdId, ...thresholdPathValues] = path.split('.');
  const thresholdPath = thresholdPathValues.join('.');

  const newThreshold = assignDefaultsToThreshold({
    path: thresholdPath,
    threshold: {
      actionId: thresholdOwnerActionId ?? ui.focusedNodeId,
      left: null,
      operator: null,
      publicId: thresholdId,
      right: null,
    },
    workflowData: (workflowData as SchemaInstance),
    workflowProps,
    value: value.leftActionId,
    featureFlags: featureFlagsForStore,
  });

  if (isSelfReferencingThreshold(newThreshold)) {
    captureSelfReferencingThresholdError({
      thresholdIds: {
        actionId: newThreshold?.actionId,
        leftActionId: newThreshold?.left?.actionId,
        publicId: newThreshold?.publicId,
        rightActionId: newThreshold?.right?.actionId,
      },
      userAction: 'create',
    });
    return;
  }

  workflowData.thresholds[thresholdId] = {
    ...newThreshold,
    isPreview: Boolean(isPreview),
  };

  updateBooleanRuleWithNewThreshold({
    eventValue: {
      actionId: thresholdOwnerActionId ?? ui.focusedNodeId,
    },
    thresholdId,
    workflowData,
  });

  state.workflowData = compileWorkflowDataDependencies(workflowData);

  if (!isPreview) {
    state.workflowSnapshots = workflowSnapshots;
  }
}

export function removeThreshold(
  state: WorkflowState,
  action: {
    payload: { id: string };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { id: thresholdId } = action.payload;
  const threshold = workflowData.thresholds[thresholdId];

  if (!threshold) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_THRESHOLD_ID);
  }

  if (isSelfReferencingThreshold(threshold)) {
    captureSelfReferencingThresholdError({
      thresholdIds: {
        actionId: threshold?.actionId,
        leftActionId: threshold?.left?.actionId,
        publicId: threshold?.publicId,
        rightActionId: threshold?.right?.actionId,
      },
      userAction: 'remove',
    });
  }

  deleteProperty(workflowData.thresholds, thresholdId);
  modifyRelevantDataFromThresholdRemoval({ thresholdId, workflowData });

  state.workflowData = compileWorkflowDataDependencies(workflowData);

  if (!threshold.isPreview) {
    state.workflowSnapshots = workflowSnapshots;
  }
}

export function updateThreshold(
  state: WorkflowState,
  action: {
    payload: {
      path?: string;
      value: any;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { workflowProps, featureFlagsForStore } = state;
  const { path, value } = action.payload;
  const [thresholdId, ...thresholdPathValues] = path.split('.');
  const thresholdPath = thresholdPathValues.join('.');
  const originalThreshold = workflowData.thresholds[thresholdId];

  if (!originalThreshold) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_THRESHOLD_ID);
  }


  if (isSelfReferencingThreshold(originalThreshold)) {
    captureSelfReferencingThresholdError({
      thresholdIds: {
        actionId: originalThreshold?.actionId,
        leftActionId: originalThreshold?.left?.actionId,
        publicId: originalThreshold?.publicId,
        rightActionId: originalThreshold?.right?.actionId,
      },
      userAction: 'update',
      meta: {
        originalThreshold: true,
      },
    });
  }

  if (['left.actionId', 'left.attributeId'].includes(thresholdPath)) {
    const newThreshold = assignDefaultsToThreshold({
      path: thresholdPath,
      threshold: originalThreshold,
      workflowData: (workflowData as SchemaInstance),
      workflowProps,
      value,
      featureFlags: featureFlagsForStore,
    });
    workflowData.thresholds[thresholdId] = newThreshold;
    if (isSelfReferencingThreshold(newThreshold)) {
      captureSelfReferencingThresholdError({
        thresholdIds: {
          actionId: newThreshold?.actionId,
          leftActionId: newThreshold?.left?.actionId,
          publicId: newThreshold?.publicId,
          rightActionId: newThreshold?.right?.actionId,
        },
        userAction: 'update',
        meta: {
          left: true,
          originalThreshold: false,
        },
      });
    }
  } else if (thresholdPath === 'right.actionId') {
    const newThreshold = assignDynamicComparisonRightSide({
      threshold: originalThreshold,
      rightActionId: value,
      workflowData,
      workflowProps,
    });
    workflowData.thresholds[thresholdId] = newThreshold;
    if (isSelfReferencingThreshold(newThreshold)) {
      captureSelfReferencingThresholdError({
        thresholdIds: {
          actionId: newThreshold?.actionId,
          leftActionId: newThreshold?.left?.actionId,
          publicId: newThreshold?.publicId,
          rightActionId: newThreshold?.right?.actionId,
        },
        userAction: 'update',
        meta: {
          right: true,
          originalThreshold: false,
        },
      });
    }
  } else {
    const updatedThreshold = updateThresholdValuesBasedOnDataType({
      path: thresholdPath,
      threshold: originalThreshold,
      workflowData: (workflowData as SchemaInstance),
      workflowProps,
      value,
    });
    workflowData.thresholds = setProperty(workflowData.thresholds, thresholdId, updatedThreshold);
  }

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function switchThresholdToDynamicComparison(
  state: WorkflowState,
  action: {
    payload: {
      thresholdId: string;
      nodeContainerState: WorkflowState['ui']['nodeContainers'];
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { workflowProps } = state;
  const { thresholdId, nodeContainerState } = action.payload;
  const originalThreshold = workflowData.thresholds[thresholdId];

  if (!originalThreshold) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_THRESHOLD_ID);
  }

  /**
   * we build a relationship bundle to grab all downstream actions the threshold owner is connected to.
   * while building a graph just for this seems a bit much, the performance impact is negligible
   */
  const graph = buildGraph(state.workflowData.actions, state.workflowData.dependencies);
  const graphTopology = getBidirectionalGraphTopology(
    graph,
    nodeContainerState,
  );
  const nodeLineage = getAllNodeIdsConnectedToNode(
    originalThreshold.actionId, graphTopology,
  );

  const actionIdsConnectedDownstreamFromThresholdOwner = new Set<string>([
    ...nodeLineage.directOutgoers,
    ...nodeLineage.extendedOutgoers,
  ]);

  const newThreshold = assignDynamicComparisonRightSide({
    threshold: originalThreshold,
    workflowData,
    workflowProps,
    actionIdsConnectedDownstreamFromThresholdOwner,
  });
  workflowData.thresholds[thresholdId] = newThreshold;

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}


export function switchThresholdToStaticComparison(
  state: WorkflowState,
  action: {
    payload: {
      thresholdId: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { workflowProps, featureFlagsForStore } = state;
  const { thresholdId } = action.payload;
  const originalThreshold = workflowData.thresholds[thresholdId];

  if (!originalThreshold) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_THRESHOLD_ID);
  }

  const newThreshold = removeDynamicComparisonRightSide({
    threshold: originalThreshold,
    workflowData,
    workflowProps,
    featureFlags: featureFlagsForStore,
  });
  workflowData.thresholds[thresholdId] = newThreshold;

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}
